import React from 'react';
import styled from 'styled-components';
import { Minus, Plus, UtensilsCrossed } from 'lucide-react';
import DynamicProfileImage from 'components/commons/DynamicProfileImage';
import Text from 'components/_UI/Text';
import { Column } from 'styled/alignment/Column';
import { FullyCentered } from 'styled/alignment/Center';
import colors from 'theme/colors';
import currency from 'helpers/currency';
import breakpoints from 'theme/breakpoints';

const getActiveBatch = (product) => {
    if (!product?.batches?.length) return null;
    return product.batches.find((b) => b.is_active) || product.batches[0];
};

export default function ProductCard({ product, quantity, onIncrement, onDecrement }) {
    const batch = getActiveBatch(product);
    const maxQuantity = Math.min(15, product?.quantity || batch?.quantity_available || 0);
    const isSelected = quantity > 0;
    const isLowStock = product?.quantity <= 15;
    const hasDiscount = batch && batch.original_price && batch.original_price !== batch.price;
    const isFree = batch && batch.price === 0;

    return (
        <CardWrapper isSelected={isSelected}>
            <CardContent>
                <ImageWrapper>
                    {product?.image ? (
                        <DynamicProfileImage
                            variant="squired"
                            size="80px"
                            img={product.image}
                            word={product.name}
                        />
                    ) : (
                        <PlaceholderImage>
                            <UtensilsCrossed size={28} color={colors.nightLight} />
                        </PlaceholderImage>
                    )}
                </ImageWrapper>

                <InfoSection>
                    <Text type="bold" name="small" paddingBottom={0} color={colors.nightDark}>
                        {product?.name}
                    </Text>

                    {product?.description && (
                        <Text name="small" paddingBottom={0} color={colors.night} marginTop="2px">
                            {product.description}
                        </Text>
                    )}

                    <PriceRow>
                        {isFree ? (
                            <Text type="bold" name="small" paddingBottom={0} color={colors.success}>
                                Gratuito
                            </Text>
                        ) : hasDiscount ? (
                            <>
                                <Text
                                    name="small"
                                    paddingBottom={0}
                                    color={colors.nightLight}
                                    decoration="line-through"
                                    marginTop="4px"
                                >
                                    {currency.format(batch.original_price)}
                                </Text>
                                <Text
                                    type="bold"
                                    name="small"
                                    paddingBottom={0}
                                    color={colors.success}
                                    marginTop="4px"
                                    marginLeft="6px"
                                >
                                    {currency.format(batch.price)}
                                </Text>
                            </>
                        ) : batch ? (
                            <Text type="bold" name="small" paddingBottom={0} color={colors.nightDark} marginTop="4px">
                                {currency.format(batch.price)}
                            </Text>
                        ) : null}
                    </PriceRow>

                    {isLowStock && (
                        <LowStockBadge>
                            <Text name="small" paddingBottom={0} color={colors.occasionalOrange}>
                                Poucas unidades
                            </Text>
                        </LowStockBadge>
                    )}
                </InfoSection>

                <QuantityControls>
                    <QuantityButton
                        disabled={quantity <= 0}
                        onClick={onDecrement}
                    >
                        <Minus size={16} color={quantity > 0 ? colors.neutralLight : colors.nightLight} />
                    </QuantityButton>

                    <QuantityValue>{quantity}</QuantityValue>

                    <QuantityButton
                        disabled={quantity >= maxQuantity}
                        onClick={onIncrement}
                    >
                        <Plus
                            size={16}
                            color={quantity < maxQuantity ? colors.neutralLight : colors.nightLight}
                        />
                    </QuantityButton>
                </QuantityControls>
            </CardContent>
        </CardWrapper>
    );
}

const CardWrapper = styled.div`
    padding: 12px;
    border-radius: 12px;
    background-color: ${({ isSelected }) => (isSelected ? '#F5EEFE' : colors.neutralLight)};
    border: 1.5px solid ${({ isSelected }) => (isSelected ? `${colors.occasionalPurple}40` : colors.neutralDark)};
    transition: background-color 0.2s ease, border-color 0.2s ease;
    width: 100%;
    box-sizing: border-box;

    & + & {
        margin-top: 10px;
    }
`;

const CardContent = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 12px;
    width: 100%;
`;

const ImageWrapper = styled.div`
    flex-shrink: 0;
    width: 80px;
    height: 80px;
    border-radius: 8px;
    overflow: hidden;
`;

const PlaceholderImage = styled(FullyCentered)`
    width: 80px;
    height: 80px;
    border-radius: 8px;
    background-color: ${colors.neutralDark};
`;

const InfoSection = styled(Column)`
    flex: 1;
    min-width: 0;
    justify-content: center;
`;

const PriceRow = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
    flex-wrap: wrap;
`;

const LowStockBadge = styled.div`
    display: inline-flex;
    align-items: center;
    margin-top: 4px;
    padding: 2px 8px;
    border-radius: 4px;
    background-color: ${colors.occasionalOrange}1A;
    width: fit-content;
`;

const QuantityControls = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 8px;
    flex-shrink: 0;
`;

const QuantityButton = styled.button`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    border-radius: 8px;
    border: none;
    cursor: ${({ disabled }) => (disabled ? 'default' : 'pointer')};
    background-color: ${({ isActive, disabled }) =>
        disabled ? colors.neutralDark : colors.occasionalPurple};
    opacity: ${({ disabled }) => (disabled ? 0.6 : 1)};
    transition: opacity 0.15s ease, background-color 0.15s ease;

    &:active:not(:disabled) {
        opacity: 0.8;
    }
`;

const QuantityValue = styled.span`
    min-width: 20px;
    text-align: center;
    font-weight: 700;
    font-size: 14px;
    color: ${colors.nightDark};
`;
